import {
  LIVE_BREAK_BLOCKED_BY,
  LIVE_BREAK_CLOSE_REASONS,
  LIVE_BREAK_ENDED_BY,
  LIVE_BREAK_ENTRY_STATUSES,
  LIVE_BREAK_HOUSE_PROFILES,
  LIVE_BREAK_QUEUE_TIERS,
  LIVE_BREAK_RELEASE_REASONS,
  LIVE_BREAK_REVEAL_PHASES,
  LIVE_BREAK_STATUSES,
} from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import {
  type AnyPgColumn,
  bigint,
  boolean,
  check,
  customType,
  index,
  integer,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";

import { balanceLedger, boxes, boxVersions, endUsers, environments, openGroups } from "./app.js";
import { tenant, user } from "./auth.js";

/**
 * `text` collated "C": compared and sorted byte by byte, never by the database's locale, which
 * under a UTF-8 collation ignores punctuation and would sort `/#…` after `1#…`.
 */
const byteOrderedText = customType<{ data: string }>({
  dataType: () => 'text COLLATE "C"',
});

/** Re-exported so the tables and their readers keep one import; declared in api-contract (the wire carries them). */
export {
  LIVE_BREAK_BLOCKED_BY,
  LIVE_BREAK_CLOSE_REASONS,
  LIVE_BREAK_ENDED_BY,
  LIVE_BREAK_ENTRY_STATUSES,
  LIVE_BREAK_HOUSE_PROFILES,
  LIVE_BREAK_QUEUE_TIERS,
  LIVE_BREAK_RELEASE_REASONS,
  LIVE_BREAK_REVEAL_PHASES,
  LIVE_BREAK_STATUSES,
};

// ---- live breaks ------------------------------------------------------------------------------
// A live break is a session in which a host's shoppers buy boxes into a queue and the boxes open
// on stage, one queue entry at a time, for every viewer (docs/live-breaks.md). The environment's
// settings are `storefront_configs.live_break_settings`.

/**
 * One end user's standing as a live-break host in an environment: their own commission rate and
 * whether they are on the Host Allow List. A row exists for anyone who has hosted, set a rate or
 * been allowed. Writer: tenant staff (the allow-list, rates; dashboard), the host (their own rate,
 * through `/v1`). Staff-facing; a host reads their own row's rate. `allowed_by_user_id` names a
 * dashboard user and never reaches a shopper.
 *
 * Removal from the list clears `allowed_at` and stops future live breaks only: open ones keep
 * running, and the host keeps their history and commission.
 */
export const liveBreakHosts = pgTable(
  "live_break_hosts",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    /**
     * The host's own rate in basis points; NULL = the environment's default rate. Read capped at
     * the environment's maximum rate (`effectiveLiveBreakRateBps`), never rewritten when the
     * maximum drops.
     */
    rateBps: integer("rate_bps"),
    /** When staff put the host on the Host Allow List; NULL = not on it, or removed. */
    allowedAt: timestamp("allowed_at", { withTimezone: true }),
    /** The dashboard user who allowed them; set with `allowed_at`. */
    allowedByUserId: text("allowed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("live_break_hosts_env_end_user_idx").on(t.environmentId, t.endUserId),
    index("live_break_hosts_allowed_idx")
      .on(t.environmentId, t.allowedAt)
      .where(sql`${t.allowedAt} IS NOT NULL`),
    check(
      "live_break_hosts_rate_bps_range",
      sql`${t.rateBps} IS NULL OR ${t.rateBps} BETWEEN 0 AND 300`,
    ),
  ],
);

/**
 * One live break. Writer: its host (through `/v1` with a session), tenant staff (dashboard), and
 * the tick (phase steps, presence of a house live break, close). `host_end_user_id` NULL is a
 * house live break, run by the store in Auto Mode, earning no commission. Every column is safe for
 * the tenant's staff; shoppers see the host only as a public profile, never the id's end user row.
 *
 * Only `open → closed` is a status; Purchases stopped, Draining, host presence, Driving and Holding
 * are derived while open (`core/src/liveBreaks/liveBreakState.ts`). Every timed step is fenced on
 * `status`, `reveal_phase`, `presenting_entry_id` and `phase_changed_at`.
 *
 * `host_end_user_id` cascades: deleting a host's end user deletes their live breaks and, through
 * `live_break_entries.live_break_id`, every buyer's entry in them. An entry's open group
 * (`open_groups.live_break_entry_id`) and a commission accrual's open group are NO ACTION, so that
 * delete is refused while any buyer holds a reveal from one of those live breaks, rather than
 * taking other shoppers' reveals and holds with it. A tenant or sandbox delete cascades through
 * both sides in one statement and passes.
 */
export const liveBreaks = pgTable(
  "live_breaks",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** The host; NULL = a house live break. */
    hostEndUserId: text("host_end_user_id").references(() => endUsers.id, { onDelete: "cascade" }),
    /** A house live break's profile; NULL for every live break but a dev stage's `idle_end`. */
    houseProfile: text("house_profile", { enum: LIVE_BREAK_HOUSE_PROFILES }),
    /** 1–80 characters, through the chat filter (profanity, links). Public. */
    title: varchar("title", { length: 80 }).notNull(),
    /** Listing only: a private live break stays out of the directory; any live break reads by id. */
    isPublic: boolean("is_public").notNull().default(true),
    /** The box categories its boxes are in (`readBoxCategories`), written when boxes change. */
    categories: text("categories").array().notNull().default(sql`'{}'::text[]`),
    status: text("status", { enum: LIVE_BREAK_STATUSES }).notNull().default("open"),
    /** False = Purchases stopped. Never written by a host lapse. */
    isAcceptingPurchases: boolean("is_accepting_purchases").notNull().default(true),
    /** True = the tick runs the queue and host phase clicks are refused; always true for a house live break. */
    isAutoMode: boolean("is_auto_mode").notNull().default(false),
    /** Auto Mode only. Kept, inert, through a Draining, so an undone drain comes back paused. */
    isPaused: boolean("is_paused").notNull().default(false),
    /** True = sells only its live-break boxes; false = any eligible box at or above the minimum too. */
    isRestrictedToLiveBreakBoxes: boolean("is_restricted_to_live_break_boxes")
      .notNull()
      .default(false),
    /** Viewers' stage delay, to match the stream's latency; the host is exempt. */
    openerDelayMs: integer("opener_delay_ms").notNull().default(0),
    /** Queued entries, all buyers. Staff-set. */
    queueCap: integer("queue_cap").notNull().default(500),
    /** Queued entries per buyer; NULL = no limit. */
    buyerQueueCap: integer("buyer_queue_cap"),
    /** The host's rate at create, in basis points; NULL on a house live break. */
    commissionRateBps: integer("commission_rate_bps"),
    /** The minimum price of a box from outside the live-break boxes, in US cents. */
    minBoxPriceCents: integer("min_box_price_cents").notNull(),
    /** The Twitch login streamed beside it (M4). */
    twitchChannel: text("twitch_channel"),
    /** The Twitch user id behind `twitch_channel`, saved with it for the priority check. */
    twitchUserId: text("twitch_user_id"),
    revealPhase: text("reveal_phase", { enum: LIVE_BREAK_REVEAL_PHASES }).notNull().default("idle"),
    /** The entry on stage; set in `presenting` and `revealed`. */
    // Annotated: the entries reference this table back, and TypeScript cannot infer the cycle.
    presentingEntryId: text("presenting_entry_id").references(
      (): AnyPgColumn => liveBreakEntries.id,
    ),
    /** The fence and the clock of every timed step; never cleared once set. */
    phaseChangedAt: timestamp("phase_changed_at", { withTimezone: true }),
    /** Heartbeats and host writes stamp it; the tick stamps a house live break's. */
    hostLastSeenAt: timestamp("host_last_seen_at", { withTimezone: true }).notNull(),
    /** When the queue last emptied: starts the abandoned-close clock. */
    queueEmptiedAt: timestamp("queue_emptied_at", { withTimezone: true }),
    /** Draining once passed; End sets it to now; staff may schedule a house live break's. */
    endsAt: timestamp("ends_at", { withTimezone: true }),
    /** Who pressed End; NULL on a scheduled end. */
    endedBy: text("ended_by", { enum: LIVE_BREAK_ENDED_BY }),
    /** The site-chat announcement, once. */
    announcedAt: timestamp("announced_at", { withTimezone: true }),
    closedAt: timestamp("closed_at", { withTimezone: true }),
    closeReason: text("close_reason", { enum: LIVE_BREAK_CLOSE_REASONS }),
    /**
     * When the close's tail ran to the end (`finishLiveBreakClose`: buyers' claims attempted,
     * viewers' rows deleted, `LIVE_BREAK_CLOSED` published), whatever each buyer's claim answered
     * (boxed's `REMOVE openPk`). NULL on a closed live break = its close died partway, and the
     * tick's next run finishes it.
     */
    closeFinishedAt: timestamp("close_finished_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("live_breaks_directory_idx").on(t.environmentId, t.isPublic, t.status, t.createdAt),
    index("live_breaks_categories_idx").using("gin", t.categories),
    index("live_breaks_open_idx").on(t.id).where(sql`${t.status} = 'open'`),
    index("live_breaks_close_unfinished_idx")
      .on(t.closedAt)
      .where(sql`${t.status} = 'closed' AND ${t.closeFinishedAt} IS NULL`),
    index("live_breaks_host_idx").on(t.hostEndUserId, t.createdAt),
    index("live_breaks_twitch_channel_idx")
      .on(t.environmentId, t.twitchChannel)
      .where(sql`${t.status} = 'open' AND ${t.isPublic}`),
    check("live_breaks_opener_delay_range", sql`${t.openerDelayMs} BETWEEN 0 AND 30000`),
    check("live_breaks_queue_cap_range", sql`${t.queueCap} BETWEEN 5 AND 2000`),
    check(
      "live_breaks_buyer_queue_cap_range",
      sql`${t.buyerQueueCap} IS NULL OR ${t.buyerQueueCap} BETWEEN 1 AND 50`,
    ),
    check(
      "live_breaks_commission_rate_range",
      sql`${t.commissionRateBps} IS NULL OR ${t.commissionRateBps} BETWEEN 0 AND 300`,
    ),
    check(
      "live_breaks_house_has_no_rate",
      sql`(${t.hostEndUserId} IS NULL) = (${t.commissionRateBps} IS NULL)`,
    ),
    check(
      "live_breaks_profile_is_house",
      sql`${t.houseProfile} IS NULL OR ${t.hostEndUserId} IS NULL`,
    ),
    check("live_breaks_min_box_price_nonneg", sql`${t.minBoxPriceCents} >= 0`),
    check(
      "live_breaks_closed_has_reason",
      sql`(${t.status} = 'closed') = (${t.closedAt} IS NOT NULL AND ${t.closeReason} IS NOT NULL)`,
    ),
    check("live_breaks_ended_by_has_end", sql`${t.endedBy} IS NULL OR ${t.endsAt} IS NOT NULL`),
    check(
      "live_breaks_idle_has_no_entry",
      sql`(${t.revealPhase} = 'idle') = (${t.presentingEntryId} IS NULL)`,
    ),
    check("live_breaks_pause_needs_auto", sql`NOT ${t.isPaused} OR ${t.isAutoMode}`),
  ],
);

/**
 * The 1–50 boxes chosen for a live break (`assertLiveBreakBoxCount`), in display order. Writer: the
 * host (adds only) and tenant staff (replace). Public.
 */
export const liveBreakBoxes = pgTable(
  "live_break_boxes",
  {
    liveBreakId: text("live_break_id")
      .notNull()
      .references(() => liveBreaks.id, { onDelete: "cascade" }),
    /**
     * Cascades, as every list of boxes does (`box_favorites`, `box_template_enablements`): this
     * table has no environment of its own and goes with its live break, a cascade the boxes' own
     * deletion in a tenant or sandbox delete would otherwise refuse before it ran.
     */
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    position: integer("position").notNull(),
    addedAt: timestamp("added_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [primaryKey({ columns: [t.liveBreakId, t.boxId] })],
);

/**
 * One shopper's purchase in a live break of 1–5 of one box, holding one place in the queue.
 * Writer: the buyer (purchase, cancel), the host and staff (Pick, cancel, Block), the tick and the
 * close. Its hold is `amount × price_cents`, carried in `end_users.held_cents` while queued and on
 * its open group after the Reveal. Other shoppers see the buyer only as a public profile.
 * `idempotency_key` and `client_seed` are the buyer's own.
 */
export const liveBreakEntries = pgTable(
  "live_break_entries",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    liveBreakId: text("live_break_id")
      .notNull()
      .references(() => liveBreaks.id, { onDelete: "cascade" }),
    buyerEndUserId: text("buyer_end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id),
    /** The version the entry opens against, pinned at purchase. */
    boxVersionId: text("box_version_id")
      .notNull()
      .references(() => boxVersions.id),
    /** Boxes bought, all of `box_id`. */
    amount: integer("amount").notNull(),
    /** The box's own price at purchase, in US cents. */
    boxPriceCents: integer("box_price_cents").notNull(),
    /** The live-break price of one box at purchase, in US cents; rarity is graded against it. */
    priceCents: integer("price_cents").notNull(),
    /** The rate the purchase was made at, in basis points; NULL in a house live break. */
    commissionRateBps: integer("commission_rate_bps"),
    clientSeed: varchar("client_seed", { length: 36 }).notNull(),
    status: text("status", { enum: LIVE_BREAK_ENTRY_STATUSES }).notNull().default("queued"),
    /** How it got its place, not where it sorts. */
    queueTier: text("queue_tier", { enum: LIVE_BREAK_QUEUE_TIERS }).notNull(),
    /**
     * The queue order, compared byte by byte; present only while queued, and it only ever
     * decreases. Collated "C" on the column, so every comparison, sort and index orders the
     * front lane's `/#…` keys ahead of `0#…` and `1#…` whatever the database's own collation is.
     */
    queueKey: byteOrderedText("queue_key"),
    releaseReason: text("release_reason", { enum: LIVE_BREAK_RELEASE_REASONS }),
    openedAt: timestamp("opened_at", { withTimezone: true }),
    releasedAt: timestamp("released_at", { withTimezone: true }),
    /** The purchase's `Idempotency-Key`. */
    idempotencyKey: text("idempotency_key"),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("live_break_entries_queue_idx")
      .on(t.liveBreakId, t.queueKey)
      .where(sql`${t.status} = 'queued'`),
    index("live_break_entries_buyer_queued_idx")
      .on(t.buyerEndUserId, t.liveBreakId)
      .where(sql`${t.status} = 'queued'`),
    index("live_break_entries_opened_idx")
      .on(t.liveBreakId, t.openedAt)
      .where(sql`${t.status} = 'opened'`),
    index("live_break_entries_buyer_opened_idx")
      .on(t.buyerEndUserId, t.liveBreakId, t.openedAt)
      .where(sql`${t.status} = 'opened'`),
    index("live_break_entries_box_idx").on(t.boxId),
    uniqueIndex("live_break_entries_idempotency_idx")
      .on(t.environmentId, t.idempotencyKey)
      .where(sql`${t.idempotencyKey} IS NOT NULL`),
    check("live_break_entries_amount_range", sql`${t.amount} BETWEEN 1 AND 5`),
    check(
      "live_break_entries_commission_rate_range",
      sql`${t.commissionRateBps} IS NULL OR ${t.commissionRateBps} BETWEEN 0 AND 300`,
    ),
    check(
      "live_break_entries_queued_has_key",
      sql`(${t.status} = 'queued') = (${t.queueKey} IS NOT NULL)`,
    ),
    check(
      "live_break_entries_opened_has_time",
      sql`(${t.status} = 'opened') = (${t.openedAt} IS NOT NULL)`,
    ),
    check(
      "live_break_entries_released_has_reason",
      sql`(${t.status} = 'released') = (${t.releaseReason} IS NOT NULL AND ${t.releasedAt} IS NOT NULL)`,
    ),
  ],
);

/**
 * A host's commission on one opened entry, in thousandths of a cent, or the sub-cent remainder a
 * claim carried forward: the shape of `forge_commission_accruals`. Writer: the Reveal (in its own
 * transaction, keyed by the open group, so a replayed Reveal cannot accrue twice) and
 * `claimLiveBreakCommission` (`claimed_at`, `claim_ledger_id`, the carried row). Append-only but
 * for the claim stamp. `amount_millicents` never reaches a tenant-facing response unfloored.
 */
export const liveBreakCommissionAccruals = pgTable(
  "live_break_commission_accruals",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    hostEndUserId: text("host_end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    /** NULL on a carried remainder. */
    liveBreakId: text("live_break_id").references(() => liveBreaks.id, { onDelete: "cascade" }),
    /**
     * The Reveal's open group; NULL on a carried remainder. NO ACTION, so deleting a buyer whose
     * reveal earned a host commission is refused rather than deleting the host's accrual (claimed
     * or not) with it.
     */
    openGroupId: text("open_group_id").references(() => openGroups.id, { onDelete: "no action" }),
    /** True = the host bought it (self commission); false = a viewer did (viewer commission). */
    isSelf: boolean("is_self").notNull(),
    /** The claim whose sub-cent remainder this row carries. */
    carriedFromLedgerId: text("carried_from_ledger_id").references(() => balanceLedger.id),
    amountMillicents: bigint("amount_millicents", { mode: "number" }).notNull(),
    claimedAt: timestamp("claimed_at", { withTimezone: true }),
    /** The `live_break_commission` ledger row that paid this accrual out. */
    claimLedgerId: text("claim_ledger_id").references(() => balanceLedger.id),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("live_break_accruals_open_group_idx")
      .on(t.openGroupId)
      .where(sql`${t.openGroupId} IS NOT NULL`),
    // One carried remainder per pool per claim: a claim pays both pools in one ledger row and
    // carries each pool's sub-cent remainder on its own row.
    uniqueIndex("live_break_accruals_carried_idx")
      .on(t.carriedFromLedgerId, t.isSelf)
      .where(sql`${t.carriedFromLedgerId} IS NOT NULL`),
    index("live_break_accruals_unclaimed_idx")
      .on(t.hostEndUserId, t.isSelf)
      .where(sql`${t.claimedAt} IS NULL`),
    index("live_break_accruals_live_break_idx").on(t.liveBreakId),
    check(
      "live_break_accruals_one_provenance",
      sql`(${t.openGroupId} IS NULL) <> (${t.carriedFromLedgerId} IS NULL)`,
    ),
    check("live_break_accruals_amount_positive", sql`${t.amountMillicents} > 0`),
  ],
);

/**
 * Who is watching a live break now: one row per signed-in viewer, upserted by the heartbeat. A
 * viewer counts while `last_seen_at` is within `LIVE_BREAK_VIEWER_WINDOW_MS`; older rows are
 * ignored. Writer: the heartbeat route; a Block and the close delete rows. The host is not a row
 * here (`live_breaks.host_last_seen_at`). Viewers reach other shoppers only as public profiles.
 */
export const liveBreakViewers = pgTable(
  "live_break_viewers",
  {
    liveBreakId: text("live_break_id")
      .notNull()
      .references(() => liveBreaks.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    lastSeenAt: timestamp("last_seen_at", { withTimezone: true }).notNull(),
    joinedAt: timestamp("joined_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({ columns: [t.liveBreakId, t.endUserId] }),
    index("live_break_viewers_seen_idx").on(t.liveBreakId, t.lastSeenAt),
  ],
);

/**
 * A shopper a host blocked from every live break of theirs, open or future (boxed ADR 0007):
 * `host_end_user_id` NULL is the one shared set every house live break reads. Writer: the host
 * (through `/v1`) and tenant staff. `blocked_by_user_id` names a dashboard user and never reaches a
 * shopper; a blocked shopper learns only that they were removed.
 */
export const liveBreakBlocks = pgTable(
  "live_break_blocks",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** The host whose live breaks the block covers; NULL = every house live break. */
    hostEndUserId: text("host_end_user_id").references(() => endUsers.id, { onDelete: "cascade" }),
    /** The blocked shopper. */
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    blockedBy: text("blocked_by", { enum: LIVE_BREAK_BLOCKED_BY }).notNull(),
    /** The dashboard user who blocked, when staff did. */
    blockedByUserId: text("blocked_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("live_break_blocks_host_idx")
      .on(t.environmentId, t.hostEndUserId, t.endUserId)
      .where(sql`${t.hostEndUserId} IS NOT NULL`),
    uniqueIndex("live_break_blocks_house_idx")
      .on(t.environmentId, t.endUserId)
      .where(sql`${t.hostEndUserId} IS NULL`),
  ],
);

/**
 * A staff-curated, named list of boxes a host copies into a new live break (Suggested). At most
 * `LIVE_BREAK_MAX_PLAYLISTS` per environment. Writer: tenant staff. Public.
 */
export const liveBreakPlaylists = pgTable(
  "live_break_playlists",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** Immutable, from the name at create; unique per environment, so "Hits!" and "hits" clash. */
    slug: text("slug").notNull(),
    name: varchar("name", { length: 40 }).notNull(),
    /** Display order. */
    position: integer("position").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [uniqueIndex("live_break_playlists_env_slug_idx").on(t.environmentId, t.slug)],
);

/** A playlist's boxes, at most `LIVE_BREAK_MAX_PLAYLIST_BOXES`, in order. Writer: tenant staff. */
export const liveBreakPlaylistBoxes = pgTable(
  "live_break_playlist_boxes",
  {
    playlistId: text("playlist_id")
      .notNull()
      .references(() => liveBreakPlaylists.id, { onDelete: "cascade" }),
    /** Cascades, for the reason `live_break_boxes.box_id` does. */
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    position: integer("position").notNull(),
  },
  (t) => [primaryKey({ columns: [t.playlistId, t.boxId] })],
);

/**
 * The tick's lease: one row (`id = 'tick'`), the `notification_dispatch_state` pattern. A run takes
 * it by bumping `generation` and stops once a newer run holds a higher one; the fence on every
 * step, not the lease, is what keeps a step from running twice. `idle_end_spawned_at` is the dev
 * stage's respawn claim. Writer: the tick. Platform-internal, never tenant-facing.
 */
export const liveBreakTickState = pgTable("live_break_tick_state", {
  id: text("id").primaryKey(),
  generation: integer("generation").notNull().default(0),
  idleEndSpawnedAt: timestamp("idle_end_spawned_at", { withTimezone: true }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
